import { useState, useEffect } from 'react';
import { offlineQueue } from '../../utils/offlineSync';
import { useI18n } from '../../contexts/I18nContext';

/**
 * Floating offline status bar + sync indicator.
 * Shows when offline or when pending items exist in the queue.
 */
const OfflineIndicator = () => {
  const { t } = useI18n();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncStatus, setSyncStatus] = useState(null); // syncing | synced | error
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const handleOnline = () => { setIsOnline(true); setShowBanner(true); setTimeout(() => setShowBanner(false), 3000); };
    const handleOffline = () => { setIsOnline(false); setShowBanner(true); };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Listen for sync events from the queue
    const unsub = offlineQueue.onUpdate((event) => {
      switch (event.type) {
        case 'sync_start': setSyncStatus('syncing'); break;
        case 'sync_complete':
          setSyncStatus('synced');
          setPendingCount(event.remaining);
          setTimeout(() => setSyncStatus(null), 3000);
          break;
        case 'sync_error': setSyncStatus('error'); break;
        default: break;
      }
    });

    // Check pending count periodically
    const interval = setInterval(async () => {
      try { const count = await offlineQueue.getCount(); setPendingCount(count); } catch { /* ignore */ }
    }, 5000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      unsub();
      clearInterval(interval);
    };
  }, []);

  // Don't show if online and nothing pending
  if (isOnline && pendingCount === 0 && !showBanner && !syncStatus) return null;

  return (
    <>
      {/* Offline Banner */}
      {!isOnline && (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 10000,
          background: 'linear-gradient(90deg, #C53030, #E53E3E)',
          color: '#fff', padding: '0.6rem 1rem',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem',
          fontSize: '0.85rem', fontWeight: 600,
          fontFamily: "'DM Sans', sans-serif",
          boxShadow: '0 -2px 10px rgba(0,0,0,0.15)',
        }}>
          <i className="bi bi-wifi-off" style={{ fontSize: '1.1rem' }}></i>
          {t('off_offline')}
          {pendingCount > 0 && (
            <span style={{
              background: 'rgba(255,255,255,0.2)', padding: '0.15rem 0.5rem',
              borderRadius: '12px', fontSize: '0.75rem',
            }}>{pendingCount} pending</span>
          )}
        </div>
      )}

      {/* Online + pending sync */}
      {isOnline && (syncStatus === 'syncing' || pendingCount > 0) && (
        <div style={{
          position: 'fixed', bottom: 12, right: 12, zIndex: 9998,
          background: '#1B3A5C', color: '#fff', padding: '0.5rem 1rem',
          borderRadius: '10px', fontSize: '0.8rem', fontWeight: 600,
          fontFamily: "'DM Sans', sans-serif",
          boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
          display: 'flex', alignItems: 'center', gap: '0.5rem',
        }}>
          {syncStatus === 'syncing' ? (
            <><span className="spinner-border spinner-border-sm"></span> {t('off_syncing', { n: pendingCount })}</>
          ) : (
            <>
              <i className="bi bi-cloud-arrow-up"></i> {t('off_pending', { n: pendingCount })}
              <button onClick={() => offlineQueue.sync()} style={{
                background: '#1E6FBA', border: 'none', color: '#fff', padding: '0.2rem 0.5rem',
                borderRadius: '6px', fontSize: '0.7rem', fontWeight: 700, cursor: 'pointer',
              }}>{t('off_syncNow')}</button>
            </>
          )}
        </div>
      )}

      {/* Back online toast */}
      {isOnline && showBanner && pendingCount === 0 && !syncStatus && (
        <div style={{
          position: 'fixed', bottom: 12, right: 12, zIndex: 9998,
          background: 'linear-gradient(90deg, #276749, #38A169)', color: '#fff',
          padding: '0.5rem 1rem', borderRadius: '10px', fontSize: '0.85rem', fontWeight: 600,
          fontFamily: "'DM Sans', sans-serif",
          boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
          display: 'flex', alignItems: 'center', gap: '0.5rem',
          animation: 'slideUp 0.3s ease-out',
        }}>
          <i className="bi bi-wifi"></i> {t('off_backOnline')}
        </div>
      )}

      <style>{`
        @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>
    </>
  );
};

export default OfflineIndicator;
